import { useMemo } from "react";
import { cn } from "../utils";
import { Button } from "./Button";
import { Icon } from "../icons";
import { showToast } from "./Toast";

export interface SocialShareProps {
  url?: string;
  title?: string;
  className?: string;
  showCopy?: boolean;
  layout?: "inline" | "stack";
}

const providers = [
  { name: "Facebook", url: (u: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`, label: "Share on Facebook" },
  { name: "Twitter", url: (u: string, t?: string) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}${t ? `&text=${encodeURIComponent(t)}` : ""}`, label: "Share on Twitter" },
  { name: "LinkedIn", url: (u: string, t?: string) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}${t ? `&title=${encodeURIComponent(t)}` : ""}`, label: "Share on LinkedIn" },
  { name: "Email", url: (u: string, t?: string) => `mailto:?subject=${encodeURIComponent(t || "Check this out")}&body=${encodeURIComponent(u)}`, label: "Share via Email" },
];

export function SocialShare({ url, title, className, showCopy = true, layout = "inline" }: SocialShareProps) {
  const shareUrl = useMemo(() => url || (typeof window !== "undefined" ? window.location.href : ""), [url]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast({ title: "Link copied", description: "Share it anywhere.", variant: "success" });
    } catch {
      showToast({ title: "Could not copy", description: "Copy the link manually.", variant: "error" });
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2 flex-wrap",
        layout === "stack" && "flex-col items-start",
        className
      )}
    >
      {providers.map((p) => (
        <a
          key={p.name}
          href={p.url(shareUrl, title)}
          target={p.name === "Email" ? undefined : "_blank"}
          rel="noreferrer"
        >
          <Button
            variant="ghost"
            size="sm"
            className="sf-pressable sf-icon-animate rounded-full border border-white/10 bg-white/5 text-[var(--color-text,#0f172a)] gap-2 px-4"
          >
            <span className="sf-icon-animate text-base text-primary">
              {p.name === "Facebook" && <Icon name="facebook" className="h-4 w-4" />}
              {p.name === "Twitter" && <Icon name="twitter" className="h-4 w-4" />}
              {p.name === "LinkedIn" && <Icon name="linkedin" className="h-4 w-4" />}
              {p.name === "Email" && <Icon name="mail" className="h-4 w-4" />}
            </span>
            <span className="text-sm font-semibold">{p.name}</span>
          </Button>
        </a>
      ))}
      {showCopy && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="sf-pressable sf-icon-animate rounded-full border border-white/10 bg-white/5 text-[var(--color-text,#0f172a)] gap-2 px-4"
        >
          <span className="sf-icon-animate text-base text-primary">
            <Icon name="link" className="h-4 w-4" />
          </span>
          Copy link
        </Button>
      )}
    </div>
  );
}
